import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getMyProfile, updateMyProfile } from "@/lib/account.functions";
import { supabase } from "@/integrations/supabase/client";
import { Camera, Save } from "lucide-react";

export const Route = createFileRoute("/_authenticated/konto/profil")({
  component: ProfilePage,
});

type Form = {
  display_name: string;
  phone: string;
  address: string;
  postal_code: string;
  city: string;
  bio: string;
  avatar_url: string;
};

function ProfilePage() {
  const qc = useQueryClient();
  const getProfile = useServerFn(getMyProfile);
  const update = useServerFn(updateMyProfile);
  const { data, isLoading } = useQuery({ queryKey: ["my-profile"], queryFn: () => getProfile() });

  const [form, setForm] = useState<Form>({
    display_name: "", phone: "", address: "", postal_code: "", city: "", bio: "", avatar_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.profile) return;
    const p = data.profile;
    setForm({
      display_name: p.display_name ?? "",
      phone: p.phone ?? "",
      address: p.address ?? "",
      postal_code: p.postal_code ?? "",
      city: p.city ?? "",
      bio: p.bio ?? "",
      avatar_url: p.avatar_url ?? "",
    });
  }, [data]);

  // Refresh signed URL for avatar
  useEffect(() => {
    let ignore = false;
    async function refresh() {
      if (!form.avatar_url) { setAvatarUrl(null); return; }
      if (form.avatar_url.startsWith("http")) { setAvatarUrl(form.avatar_url); return; }
      const { data: signed } = await supabase.storage.from("user-content").createSignedUrl(form.avatar_url, 60 * 60);
      if (!ignore) setAvatarUrl(signed?.signedUrl ?? null);
    }
    refresh();
    return () => { ignore = true; };
  }, [form.avatar_url]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) throw new Error("Ej inloggad");
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${uid}/avatar-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("user-content").upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      setForm((f) => ({ ...f, avatar_url: path }));
      await update({ data: { ...form, avatar_url: path } });
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Bild uppladdad");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Uppladdning misslyckades");
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await update({ data: form });
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Profil sparad");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className="rounded-3xl border border-border bg-card p-8">Laddar…</div>;

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-6 md:p-8 elev-shadow">
      <div className="flex flex-wrap items-center gap-6">
        <div className="relative">
          <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-full bg-secondary text-3xl font-display text-muted-foreground">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profil" className="h-full w-full object-cover" />
            ) : (
              (form.display_name || data?.email || "?").slice(0, 1).toUpperCase()
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground shadow disabled:opacity-60"
          >
            <Camera size={15} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </div>
        <div>
          <p className="font-display text-2xl">{form.display_name || "Namnlös"}</p>
          <p className="text-sm text-muted-foreground">{data?.email}</p>
        </div>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Input label="Namn" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
        <Input label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" />
        <Input label="Adress" value={form.address} onChange={(v) => setForm({ ...form, address: v })} className="sm:col-span-2" />
        <Input label="Postnummer" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} />
        <Input label="Ort" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
      </div>

      <div className="mt-5">
        <label className="mb-2 block text-sm font-medium">Om mig</label>
        <textarea
          rows={4}
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          placeholder="Berätta lite om dig själv, din husbil och favoritresorna…"
          className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
      </div>

      <button
        disabled={saving}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
      >
        <Save size={15} />
        {saving ? "Sparar…" : "Spara profil"}
      </button>
    </form>
  );
}

function Input({
  label, value, onChange, type = "text", className = "",
}: { label: string; value: string; onChange: (v: string) => void; type?: string; className?: string }) {
  return (
    <div className={className}>
      <label className="mb-2 block text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}
